import type { SignalDto } from '@terra/shared-contracts-signals';
import type {
  ScenarioDto,
  InternalCrownJewelDto,
} from '@terra/shared-contracts-internal-network';
import { makeAdGraphNodeId } from '@terra/shared-contracts-internal-network';
import { InternalNetworkAdEntityKindSchema } from '@terra/shared-schemas-orchestrator';

function readMetadataString(
  metadata: SignalDto['metadata'],
  key: string,
): string | null {
  if (
    metadata === null ||
    typeof metadata !== 'object' ||
    Array.isArray(metadata)
  ) {
    return null;
  }

  const value = metadata[key];
  return typeof value === 'string' ? value : null;
}

function resolveAdEntityKind(value: string | null) {
  if (!value) {
    return null;
  }

  const parsed = InternalNetworkAdEntityKindSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

function toCenterNodeId({
  entityKind,
  objectId,
}: {
  entityKind: string | null;
  objectId: string | null;
}): string | null {
  const parsedEntityKind = resolveAdEntityKind(entityKind);
  if (!parsedEntityKind || !objectId) {
    return null;
  }

  return makeAdGraphNodeId({
    entityKind: parsedEntityKind,
    objectId,
  });
}

export function resolveScenarioAttackPathCenterNodeId({
  scenario,
  signal,
  crownJewels,
}: {
  scenario: ScenarioDto;
  signal: SignalDto;
  crownJewels: InternalCrownJewelDto[];
}): string | null {
  if (scenario.crownJewelId) {
    const jewel = crownJewels.find(
      (crownJewel) => crownJewel.id === scenario.crownJewelId,
    );
    if (jewel) {
      return makeAdGraphNodeId({
        entityKind: jewel.entityKind,
        objectId: jewel.entityId,
      });
    }
  }

  const metadataCenterNodeId = toCenterNodeId({
    entityKind: readMetadataString(signal.metadata, 'targetEntityKind'),
    objectId: readMetadataString(signal.metadata, 'targetEntityId'),
  });
  if (metadataCenterNodeId) {
    return metadataCenterNodeId;
  }

  const assetCenterNodeId = toCenterNodeId({
    entityKind: signal.assetType,
    objectId: signal.assetId,
  });
  if (assetCenterNodeId) {
    return assetCenterNodeId;
  }

  return null;
}
